import React from "react";
import ContractEditDrawer from "components/Contracts/ContractEditDrawer";
import GroupEdit from "components/Groups/GroupEditModal";
import CalendarEditModal from "components/Calendars/CalendarEditModal";
import ShiftEditModal from "components/Shifts/ShiftEditModal";
import ShiftPatternEditModal from "components/ShiftPatterns/ShiftPatternEditModal";
import EmployeeEditModal from "components/Employees/EmployeeEditModal";
import { openEmployeeDrawer } from "components/Employees/EmployeeEditDrawer";
import BulkAddEmployeesModal from "components/Onboarding/organisms/BulkAddEmployeesModal";

/**
 * Factory de handlers para abrir modales de creación/edición desde el onboarding.
 * Reutiliza los mismos componentes que las páginas de administración.
 */
export function createOnboardingModalHandlers({
	showModal,
	hideModal,
	t,
	onRefresh,
}) {
	const closeModal = () => hideModal();
	const refresh = () => {
		if (typeof onRefresh === "function") {
			onRefresh();
		}
	};

	return {
		openCreateContract: () => {
			showModal(
				{
					open: true,
					width: 1000,
					content: ({ close, registerBeforeClose }) => (
						<ContractEditDrawer
							getContracts={refresh}
							handleClose={close}
							registerBeforeClose={registerBeforeClose}
						/>
					),
					closeModal,
				},
				"drawer"
			);
		},

		openEditContract: (contract) => {
			showModal(
				{
					open: true,
					width: 1000,
					content: ({ close, registerBeforeClose }) => (
						<ContractEditDrawer
							contract={contract}
							getContracts={refresh}
							handleClose={close}
							registerBeforeClose={registerBeforeClose}
						/>
					),
					closeModal,
				},
				"drawer"
			);
		},

		openCreateGroup: () => {
			showModal(
				{
					open: true,
					title: t("groups.newGroup"),
					style: { width: "400px" },
					content: (
						<GroupEdit getGroups={refresh} handleClose={closeModal} />
					),
					closeModal,
				},
				"edit"
			);
		},

		openEditGroup: (group) => {
			const title = group.is_multigroup
				? t("groups.editMultigroup")
				: t("groups.editGroup");

			showModal(
				{
					open: true,
					title,
					style: { width: "400px" },
					content: (
						<GroupEdit
							group={group}
							getGroups={refresh}
							handleClose={closeModal}
						/>
					),
					closeModal,
				},
				"edit"
			);
		},

		openCreateCalendar: () => {
			showModal(
				{
					open: true,
					title: t("calendars.newCalendar"),
					style: { width: "670px" },
					content: (
						<CalendarEditModal handleClose={closeModal} />
					),
					closeModal,
				},
				"edit"
			);
		},

		openEditCalendar: (calendar) => {
			showModal(
				{
					open: true,
					title: t("calendars.editCalendar"),
					style: { width: "670px" },
					content: (
						<CalendarEditModal
							calendar={calendar}
							handleClose={closeModal}
						/>
					),
					closeModal,
				},
				"edit"
			);
		},

		openCreateShift: (initialInterval) => {
			showModal(
				{
					open: true,
					title: t("shifts.newShift"),
					style: { width: "600px" },
					content: (
						<ShiftEditModal
							getShifts={refresh}
							handleClose={closeModal}
							initialInterval={initialInterval}
							initialType={
								initialInterval ? "Trabajo" : undefined
							}
						/>
					),
					closeModal,
				},
				"edit"
			);
		},

		openEditShift: (shift) => {
			showModal(
				{
					open: true,
					title: t("shifts.editShift"),
					style: { width: "640px", maxHeight: "90vh" },
					content: (
						<ShiftEditModal
							shift={shift}
							getShifts={refresh}
							handleClose={closeModal}
						/>
					),
					closeModal,
				},
				"edit"
			);
		},

		openCreateShiftPattern: () => {
			showModal(
				{
					open: true,
					title: t("shiftPatterns.newShiftPattern"),
					style: { width: "980px", maxWidth: "calc(100vw - 32px)" },
					content: (
						<ShiftPatternEditModal
							getShiftPatterns={refresh}
							handleClose={closeModal}
						/>
					),
					closeModal,
				},
				"edit"
			);
		},

		openEditShiftPattern: (shiftPattern) => {
			showModal(
				{
					open: true,
					title: t("shiftPatterns.assignment.editShiftPattern"),
					style: { width: "980px", maxWidth: "calc(100vw - 32px)" },
					content: (
						<ShiftPatternEditModal
							getShiftPatterns={refresh}
							shift_pattern={shiftPattern}
							handleClose={closeModal}
						/>
					),
					closeModal,
				},
				"edit"
			);
		},

		openCreateEmployee: () => {
			const openWithTitle = (title) => {
				showModal(
					{
						open: true,
						title,
						style: { width: "500px" },
						content: (
							<EmployeeEditModal
								handleClose={closeModal}
								setModalTitle={openWithTitle}
								reloadEmployeeData={refresh}
							/>
						),
						closeModal,
					},
					"edit"
				);
			};

			openWithTitle(t("employees.newEmployee"));
		},

		openBulkAddEmployees: () => {
			showModal(
				{
					open: true,
					title: t("bulkAddEmployees.title", "Añadir empleados"),
					style: { width: "1200px", maxWidth: "calc(100vw - 32px)" },
					content: (
						<BulkAddEmployeesModal
							handleClose={closeModal}
							onSuccess={refresh}
						/>
					),
					closeModal,
				},
				"edit"
			);
		},

		openEditEmployee: (employee) => {
			openEmployeeDrawer({
				showModal,
				employee,
				onClose: refresh,
			});
		},
	};
}

export function openOnboardingCreateModal(stepKey, handlers, options = {}) {
	switch (stepKey) {
		case "contracts":
			handlers.openCreateContract();
			break;
		case "groups":
			handlers.openCreateGroup();
			break;
		case "holidays":
			handlers.openCreateCalendar();
			break;
		case "shifts":
			handlers.openCreateShift(options.initialInterval);
			break;
		case "shiftPatterns":
			handlers.openCreateShiftPattern();
			break;
		case "employees":
			handlers.openBulkAddEmployees();
			break;
		default:
			break;
	}
}

export function openOnboardingEditModal(stepKey, item, handlers) {
	switch (stepKey) {
		case "contracts":
			handlers.openEditContract(item);
			break;
		case "groups":
			handlers.openEditGroup(item);
			break;
		case "holidays":
			handlers.openEditCalendar(item);
			break;
		case "shifts":
			handlers.openEditShift(item);
			break;
		case "shiftPatterns":
			handlers.openEditShiftPattern(item);
			break;
		case "employees":
			handlers.openEditEmployee(item);
			break;
		default:
			break;
	}
}
